import React, { useState,useEffect } from 'react';
import { IonContent, IonPage, IonSpinner, IonAlert } from '@ionic/react';
import { Link, useHistory } from 'react-router-dom';
import { auth, googleProvider, db } from '../firebaseConfig';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { IonIcon } from '@ionic/react';
import { mailOutline, lockClosedOutline } from 'ionicons/icons';

import logo from "../assets/logo.png";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (cooldownTime > 0) {
      timer = setTimeout(() => setCooldownTime(cooldownTime - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [cooldownTime]);

  const handleLoginError = (error: any) => {
    let message = t('login.genericError');
    
    switch (error.code) {
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        message = t('login.invalidCredentials');
        break;
      case 'auth/invalid-email':
        message = t('login.invalidEmail');
        break;
      case 'auth/too-many-requests':
        message = t('login.tooManyAttempts');
        break;
      case 'auth/network-request-failed':
        message = t('login.networkError');
        break;
    }
    
    return message;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      if (user.emailVerified) {
        history.push('/teacher-selection'); 
      } else {
        history.push('/email-verification'); 
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = handleLoginError(error);
      setAlertMessage(errorMessage);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          phoneNumber: user.phoneNumber || '',
          createdAt: new Date().toISOString(),
          subscriptionStatus: 'inactive',
          hasUnlimitedTokens: false,
          tokens: 1000,
          subscriptionPlan: null,
          subscriptionEndDate: null
        });
      }
      
      history.push('/teacher-selection');
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      const errorMessage = handleLoginError(error);
      setAlertMessage(errorMessage);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setResetEmail(email);
    setShowForgotPasswordModal(true);
  };

  const startCooldown = () => {
    setCooldownTime(180);
  };

  const handlePasswordReset = async () => {
    if (!resetEmail) {
      setAlertMessage(t('login.enterEmailForReset'));
      setShowAlert(true);
      return;
    }

    if (cooldownTime > 0) {
      setAlertMessage(t('login.resetCooldown', { seconds: cooldownTime }));
      setShowAlert(true);
      return;
    }

    setIsLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setAlertMessage(t('login.resetEmailSent'));
      setShowAlert(true);
      setShowForgotPasswordModal(false);
      startCooldown();
    } catch (error: any) {
      console.error('Password reset error:', error);
      const errorMessage = handleLoginError(error);
      setAlertMessage(errorMessage);
      setShowAlert(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <IonPage>
      <IonContent className="ion-padding bg-white">
        <div className="min-h-screen flex flex-col items-center justify-center px-4">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img src={logo} alt="Logo" className="mx-auto w-32 h-32 rounded-2xl shadow-lg transform hover:scale-105 transition-transform duration-300" />
              <h1 className="text-3xl font-bold mt-6 text-white">{t('login.title')}</h1>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={mailOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.email')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <div className="relative">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                    <IonIcon icon={lockClosedOutline} className="text-gray-400 w-5 h-5" />
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('login.password')}
                    disabled={isLoading}
                    required
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-[#31D526] text-white py-3 rounded-lg hover:bg-[#28b820] transition-colors duration-300 flex items-center justify-center font-medium text-lg"
                >
                  {isLoading ? <IonSpinner name="crescent" /> : t('login.loginButton')}
                </button>
              </form>

              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>

              <button
                onClick={handleGoogleSignIn}
                disabled={isLoading}
                className="w-full bg-white border border-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-50 transition-colors duration-300 flex items-center justify-center space-x-2 font-medium"
              >
                {isLoading ? (
                  <IonSpinner name="crescent" />
                ) : (
                  <>
                    <img
                      src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                      alt="Google"
                      className="w-5 h-5"
                    />
                    <span>{t('login.continueWithGoogle')}</span>
                  </>
                )}
              </button>
            </div>

            <div className="text-center space-y-4">
              <button
                onClick={handleForgotPassword}
                className="text-primary hover:text-primary-dark transition-colors duration-300 font-medium"
              >
                {t('login.forgotPassword')}
              </button>
              <p className="text-gray-600">
                {t('login.noAccount')}{' '}
                <Link 
                  to="/register" 
                  className="text-[#31D526] hover:text-[#28b820] transition-colors duration-300 font-bold text-lg"
                >
                  {t('login.register')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('login.alert')}
          message={alertMessage}
          buttons={['OK']}
        />

        <div className={`fixed inset-0 bg-black/50 flex items-center justify-center ${showForgotPasswordModal ? '' : 'hidden'}`}>
          <div className="bg-white rounded-xl p-6 mx-4 w-full max-w-md">
            <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">{t('login.resetPassword')}</h2>
            <div className="space-y-4">
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder={t('login.email')}
                disabled={isLoading}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-gray-800"
              />
              <button
                onClick={handlePasswordReset}
                disabled={isLoading || cooldownTime > 0}
                className="w-full bg-[#31D526] text-white py-3 rounded-lg hover:bg-[#28b820] transition-colors duration-300 flex items-center justify-center font-medium"
              >
                {isLoading ? (
                  <IonSpinner name="crescent" />
                ) : cooldownTime > 0 ? (
                  <span className="text-gray-800">{t('login.resetCooldown', { seconds: cooldownTime })}</span>
                ) : (
                  t('login.sendResetLink')
                )}
              </button>
              <button
                onClick={() => setShowForgotPasswordModal(false)}
                disabled={isLoading}
                className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition-colors duration-300 font-medium"
              >
                {t('login.cancel')}
              </button>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Login;
